import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import useLang from '../hooks/useLang';

const ROLE_LABELS = {
  mwenyekiti:  'Mwenyekiti',
  katibu:      'Katibu',
  mwekahazina: 'Mwekahazina',
  mwanachama:  'Mwanachama',
  // legacy
  admin:       'Kiongozi',
  treasurer:   'Mhazini',
  member:      'Mwanachama',
};

export default function Members() {
  const { T } = useLang();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [searchParams] = useSearchParams();
  const paramGroupId = searchParams.get('group_id');

  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState(paramGroupId || user?.group_id || '');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [entryFeeModal, setEntryFeeModal] = useState(null); // member object
  const [entryFeeAmount, setEntryFeeAmount] = useState('');
  const [groupEntryFee, setGroupEntryFee] = useState(0);
  const [form, setForm] = useState({ name: '', phone: '', email: '', id_number: '', role: 'mwanachama', joined_at: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // SMS modal
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsTarget, setSmsTarget] = useState('all');
  const [smsMemberId, setSmsMemberId] = useState('');
  const [smsMemberSearch, setSmsMemberSearch] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((text, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const filteredForSms = members.filter(m =>
    !smsMemberSearch ||
    m.name.toLowerCase().includes(smsMemberSearch.toLowerCase()) ||
    m.phone.includes(smsMemberSearch)
  );

  const handleSendSms = async () => {
    if (smsTarget === 'one' && !smsMemberId) {
      showToast(T.settings.toastErr, false);
      return;
    }
    const target = smsTarget === 'one'
      ? members.find(m => String(m.id) === smsMemberId)?.name || 'mwanachama'
      : 'wanachama wote';
    if (!confirm(`Utumie SMS ya kuingia kwa ${target}?`)) return;
    setSmsSending(true);
    try {
      const body = smsTarget === 'one' ? { member_id: smsMemberId } : {};
      const { data } = await api.post(`/groups/${groupId}/send-login-sms`, body);
      setShowSmsModal(false);
      setSmsTarget('all');
      setSmsMemberId('');
      setSmsMemberSearch('');
      showToast(data.message || T.common.success);
    } catch (err) {
      showToast(err.response?.data?.error || T.errLoad, false);
    } finally {
      setSmsSending(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'superadmin') {
      api.get('/groups').then((r) => setGroups(r.data));
    }
  }, [user]);

  useEffect(() => {
    if (groupId) {
      api.get(`/groups/${groupId}`).then(r => setGroupEntryFee(+r.data.entry_fee || 0)).catch(() => {});
    }
  }, [groupId]);

  const load = (inactive = showInactive) => {
    if (!groupId) { setLoading(false); return; }
    setLoading(true);
    const url = `/members?group_id=${groupId}${inactive ? '&include_inactive=1' : ''}`;
    api.get(url)
      .then((r) => setMembers(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(showInactive); }, [groupId, showInactive]);

  const handleDeactivate = async (m) => {
    if (!confirm(`Ondoa "${m.name}" kutoka kwenye kikundi?\n\nHistoria ya michango yake itabaki.`)) return;
    try {
      await api.put(`/members/${m.id}`, { status: 'inactive' });
      showToast(`${m.name} ameondolewa`);
      load(showInactive);
    } catch (err) {
      showToast(err.response?.data?.error || 'Imeshindwa', false);
    }
  };

  const handleReactivate = async (m) => {
    if (!confirm(`Rudisha "${m.name}" kama mwanachama aktif?`)) return;
    try {
      await api.put(`/members/${m.id}`, { status: 'active' });
      showToast(`${m.name} amerejeshwa`);
      load(showInactive);
    } catch (err) {
      showToast(err.response?.data?.error || 'Imeshindwa', false);
    }
  };

  const filtered = members.filter(
    (m) => m.name.toLowerCase().includes(search.toLowerCase()) || m.phone.includes(search)
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/members', { ...form, group_id: groupId });
      setShowModal(false);
      setForm({ name: '', phone: '', email: '', id_number: '', role: 'mwanachama', joined_at: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || T.members.errAdd);
    } finally {
      setSaving(false);
    }
  };

  const handleRecordEntryFee = async (e) => {
    e.preventDefault();
    if (!entryFeeAmount || !entryFeeModal) return;
    try {
      await api.post(`/members/${entryFeeModal.id}/entry-fee`, { amount: entryFeeAmount });
      setEntryFeeModal(null);
      setEntryFeeAmount('');
      load();
    } catch (err) {
      alert(err.response?.data?.error || T.members.errEntryFee);
    }
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-24 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-2xl px-5 py-3.5 text-sm font-semibold text-white shadow-[0_16px_40px_rgba(15,23,42,0.22)] ${
          toast.ok ? 'bg-green-700' : 'bg-red-600'
        }`}>
          <span className="text-lg">{toast.ok ? '✅' : '❌'}</span>
          {toast.text}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">{T.members.title}</h1>
        {groupId && isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowInactive(v => !v)}
              className={`flex items-center gap-1.5 border px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showInactive
                  ? 'border-rose-300 bg-rose-50 text-rose-700'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {showInactive ? '👁 Wote' : '👁 Wote'}
              <span className="text-xs">{showInactive ? '(pamoja wasioaktif)' : ''}</span>
            </button>
            <button
              onClick={() => setShowSmsModal(true)}
              className="flex items-center gap-1.5 border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              📤 SMS
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {T.members.addMember}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        {user?.role === 'superadmin' && (
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">{T.common.selectGroupOption}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        <input
          type="text"
          placeholder={T.members.searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 flex-1 min-w-48"
        />
      </div>

      {!groupId ? (
        <div className="text-center py-16 text-gray-400">{T.members.chooseGroupFirst}</div>
      ) : loading ? (
        <div className="text-center py-16 text-gray-500">{T.common.loading}</div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-3 md:hidden">
            {filtered.map((m) => (
              <div key={m.id} className="rounded-[24px] border border-white/75 bg-white/88 p-4 shadow-[0_14px_35px_rgba(15,23,42,0.08)]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-800">{m.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{m.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      m.status === 'inactive'
                        ? <button onClick={() => handleReactivate(m)} className="rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">↩ Rudisha</button>
                        : <button onClick={() => handleDeactivate(m)} className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600">Ondoa</button>
                    )}
                    <Link to={`/members/${m.id}`} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      {T.common.view}
                    </Link>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <MobileInfo label={T.members.role} value={ROLE_LABELS[m.role] || m.role} />
                  <MobileInfo label={T.members.totalContributed} value={new Intl.NumberFormat('sw-TZ').format(m.total_contributed)} tone="success" />
                </div>
                <div className="mt-3 rounded-2xl bg-slate-50 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{T.members.entryFee}</p>
                  <div className="mt-2">
                    {+m.entry_fee_paid ? (
                      <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                        ✓ {new Intl.NumberFormat('sw-TZ').format(m.entry_fee_amount)}
                      </span>
                    ) : groupEntryFee > 0 && isAdmin ? (
                      <button
                        onClick={() => { setEntryFeeModal(m); setEntryFeeAmount(groupEntryFee); }}
                        className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-600"
                      >
                        ✗ {T.members.paidFee}
                      </button>
                    ) : groupEntryFee > 0 ? (
                      <span className="inline-flex rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-400">✗</span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {!filtered.length && <div className="text-center py-8 text-gray-400">{T.members.noMembers}</div>}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-gray-500 text-xs uppercase">
                  <th className="text-left px-4 py-3">{T.members.memberName}</th>
                  <th className="text-left px-4 py-3">{T.members.phone}</th>
                  <th className="text-left px-4 py-3">{T.members.role}</th>
                  <th className="text-left px-4 py-3">{T.members.entryFee}</th>
                  <th className="text-right px-4 py-3">{T.members.totalContributed}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((m) => (
                  <tr key={m.id} className={`hover:bg-gray-50 ${m.status === 'inactive' ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <div className="flex items-center gap-2">
                        {m.name}
                        {m.status === 'inactive' && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">Ameondolewa</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{m.phone}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{ROLE_LABELS[m.role] || m.role}</td>
                    <td className="px-4 py-3">
                      {+m.entry_fee_paid ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✓ {new Intl.NumberFormat('sw-TZ').format(m.entry_fee_amount)}
                        </span>
                      ) : groupEntryFee > 0 && isAdmin ? (
                        <button
                          onClick={() => { setEntryFeeModal(m); setEntryFeeAmount(groupEntryFee); }}
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200"
                        >
                          ✗ {T.members.paidFee}
                        </button>
                      ) : groupEntryFee > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-400">✗</span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-green-700">
                      {new Intl.NumberFormat('sw-TZ').format(m.total_contributed)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 justify-end">
                        {isAdmin && (
                          m.status === 'inactive'
                            ? <button onClick={() => handleReactivate(m)} className="text-green-600 hover:text-green-800 text-xs font-medium">↩ Rudisha</button>
                            : <button onClick={() => handleDeactivate(m)} className="text-rose-500 hover:text-rose-700 text-xs font-medium">Ondoa</button>
                        )}
                        <Link to={`/members/${m.id}`} className="text-green-700 hover:text-green-900 text-xs">
                          {T.common.view}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-gray-400">{T.members.noMembers}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SMS Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-800">{T.members.sendSms}</h2>
              <button
                onClick={() => { setShowSmsModal(false); setSmsTarget('all'); setSmsMemberId(''); setSmsMemberSearch(''); }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:bg-gray-200"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Toggle */}
            <div className="flex rounded-xl bg-gray-100 p-1 mb-4 gap-1">
              <button
                type="button"
                onClick={() => { setSmsTarget('all'); setSmsMemberId(''); setSmsMemberSearch(''); }}
                className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
                  smsTarget === 'all' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
                }`}
              >
                👥 {T.members.allMembers} ({members.length})
              </button>
              <button
                type="button"
                onClick={() => setSmsTarget('one')}
                className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-all ${
                  smsTarget === 'one' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'
                }`}
              >
                👤 {T.members.oneBy}
              </button>
            </div>

            {/* Individual member picker */}
            {smsTarget === 'one' && (
              <div className="mb-4 space-y-2">
                <input
                  type="text"
                  placeholder={T.members.searchMember}
                  value={smsMemberSearch}
                  onChange={e => { setSmsMemberSearch(e.target.value); setSmsMemberId(''); }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                {smsMemberSearch && !smsMemberId && (
                  <div className="max-h-44 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    {filteredForSms.length === 0 && (
                      <p className="px-4 py-3 text-sm text-gray-400">{T.members.noMemberFound}</p>
                    )}
                    {filteredForSms.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => { setSmsMemberId(String(m.id)); setSmsMemberSearch(m.name); }}
                        className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-blue-50 transition-colors"
                      >
                        <span className="font-medium text-gray-800">{m.name}</span>
                        <span className="text-xs text-gray-400">{m.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
                {smsMemberId && (
                  <div className="flex items-center gap-2 rounded-xl bg-blue-50 border border-blue-200 px-3 py-2.5">
                    <span className="text-blue-600">✓</span>
                    <span className="flex-1 text-sm font-semibold text-blue-800">
                      {members.find(m => String(m.id) === smsMemberId)?.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => { setSmsMemberId(''); setSmsMemberSearch(''); }}
                      className="text-blue-400 hover:text-blue-700 text-lg leading-none"
                    >✕</button>
                  </div>
                )}
              </div>
            )}

            {/* SMS preview */}
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 mb-5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{T.members.smsPreview}</p>
              <p className="text-xs text-slate-600 italic">
                "Habari [Jina]! Msimbo wako wa kuingia M-Koba ni: <strong>123456</strong>. Halali dakika 10. Ingia hapa: http://localhost:5173"
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setShowSmsModal(false); setSmsTarget('all'); setSmsMemberId(''); setSmsMemberSearch(''); }}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                {T.common.cancel}
              </button>
              <button
                type="button"
                onClick={handleSendSms}
                disabled={smsSending || (smsTarget === 'one' && !smsMemberId)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
              >
                {smsSending ? `⏳ ${T.members.sending}` : smsTarget === 'all' ? `📤 ${T.members.sendAll}` : `📤 ${T.members.send}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5">{T.members.newMember}</h2>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.memberName} *</label>
                <input required type="text" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.phone} *</label>
                <input required type="tel" value={form.phone} placeholder="255700000000"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.email}</label>
                <input type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.role}</label>
                  <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="mwanachama">Mwanachama</option>
                    <option value="mwenyekiti">Mwenyekiti</option>
                    <option value="katibu">Katibu</option>
                    <option value="mwekahazina">Mwekahazina</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.joinDate}</label>
                  <input type="date" value={form.joined_at}
                    onChange={(e) => setForm({ ...form, joined_at: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowModal(false); setError(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-60">
                  {saving ? T.members.adding : T.common.add}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Entry Fee Modal */}
      {entryFeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-1">{T.members.recordEntryFee}</h2>
            <p className="text-sm text-gray-500 mb-5">
              <strong>{entryFeeModal.name}</strong> — {T.members.entryFeeDesc}
            </p>
            <form onSubmit={handleRecordEntryFee} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{T.members.entryFeeAmount} *</label>
                <input
                  required type="number" min="1"
                  value={entryFeeAmount}
                  onChange={e => setEntryFeeAmount(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                {groupEntryFee > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    {T.members.defaultEntryFee}: {new Intl.NumberFormat('sw-TZ').format(groupEntryFee)} TZS
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button"
                  onClick={() => { setEntryFeeModal(null); setEntryFeeAmount(''); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50">
                  {T.common.cancel}
                </button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-green-800">
                  {T.common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileInfo({ label, value, tone = 'default', capitalize = false }) {
  const tones = {
    default: 'text-slate-700',
    success: 'text-emerald-700',
  };

  return (
    <div className="rounded-2xl bg-slate-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className={`mt-1 text-sm font-medium ${tones[tone]} ${capitalize ? 'capitalize' : ''}`}>{value}</p>
    </div>
  );
}
